import { trackEvent } from "./trackEvent";

const REMEMBERED_PURCHASE_KEY = "ac_viralize_remembered_purchase";

export interface ViralizePurchase {
  transactionId: string;
  value: number;
}

export interface ViralizeCheckout extends ViralizePurchase {
  paymentMethod: "pix" | "card";
}

function isViralizePurchase(value: unknown): value is ViralizePurchase {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const purchase = value as Record<string, unknown>;
  return (
    typeof purchase.transactionId === "string" &&
    purchase.transactionId.trim() !== "" &&
    typeof purchase.value === "number" &&
    Number.isFinite(purchase.value) &&
    purchase.value > 0
  );
}

function purchaseGuardKey(transactionId: string): string {
  return `ac_viralize_purchase_tracked_${transactionId}`;
}

function hasTrackedPurchase(transactionId: string): boolean {
  try {
    return localStorage.getItem(purchaseGuardKey(transactionId)) === "1";
  } catch {
    return false;
  }
}

function markPurchaseTracked(transactionId: string): void {
  try {
    localStorage.setItem(purchaseGuardKey(transactionId), "1");
  } catch {
    // Storage can be disabled; a later retry may emit again.
  }
}

export function trackViralizeCheckout(checkout: ViralizeCheckout): boolean {
  if (!isViralizePurchase(checkout)) return false;
  if (checkout.paymentMethod !== "pix" && checkout.paymentMethod !== "card") {
    return false;
  }

  trackEvent("checkout_viralize", {
    value: checkout.value,
    currency: "BRL",
    transaction_id: checkout.transactionId,
    payment_method: checkout.paymentMethod,
  });
  return true;
}

export function trackViralizePurchase(purchase: ViralizePurchase): boolean {
  if (!isViralizePurchase(purchase)) return false;
  if (hasTrackedPurchase(purchase.transactionId)) return false;

  markPurchaseTracked(purchase.transactionId);
  trackEvent("purchase_viralize", {
    value: purchase.value,
    currency: "BRL",
    transaction_id: purchase.transactionId,
  });
  return true;
}

export function rememberViralizePurchase(purchase: ViralizePurchase): void {
  if (typeof window === "undefined" || !isViralizePurchase(purchase)) return;
  try {
    sessionStorage.setItem(REMEMBERED_PURCHASE_KEY, JSON.stringify(purchase));
  } catch {
    // Storage can be disabled; checkout must still continue to Stripe.
  }
}

export function clearRememberedViralizePurchase(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(REMEMBERED_PURCHASE_KEY);
  } catch {
    // Storage can be disabled; there is nothing actionable for checkout.
  }
}

export function consumeRememberedViralizePurchase(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(REMEMBERED_PURCHASE_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(REMEMBERED_PURCHASE_KEY);
    const purchase: unknown = JSON.parse(raw);
    if (!isViralizePurchase(purchase)) return false;
    return trackViralizePurchase(purchase);
  } catch {
    return false;
  }
}
